/**
 * From any DANI conversation into Builder or Feasibility (docs/pdlc/builder.md,
 * "From any DANI conversation"). DANI's own instructions (data-platform,
 * admin/snowflake/dani/agent) carry only a short rule: when the person wants a
 * survey, or asks whether a study is feasible, DANI ends its message with one
 * small block holding what the person wants in words.
 *
 * - `survey-start` / `survey-change`: Claude writes the survey in Builder's own
 *   shape (DANI and Claude side by side, src/lib/builder/dani/author.ts).
 * - `survey-next`: the next step for a saved survey (check, preview,
 *   feasibility, price, publish, launch, fielding), shown as a card in the chat
 *   (docs/pdlc/builder.md, "The whole survey in the chat"). It names the draft,
 *   or none for the survey the chat is about.
 * - `feasibility-start`: Studio's card sends DANI the feasibility rules, read
 *   from their plan-template rows, as a hidden message, and DANI carries on.
 *
 * Pure.
 */

/** Hand-offs that bring DANI rules to carry on with; a survey is handed to Claude instead. */
export type HandoffKind = 'feasibility-start';
/** Everything DANI can hand over and be told it cannot, a survey included. */
export type HandoffTask = HandoffKind | 'survey-start';

/** Heads Studio's message when the person cannot use what DANI started. */
export const HANDOFF_HEADER = 'STUDIO HAND-OFF (not from the person; they do not see this message)';

export const MAX_SUMMARY_CHARS = 4000;

/** The summary DANI wrote, or null while it is still writing the block. */
export function readHandoff(content: string): { summary: string } | null {
    try {
        const v = JSON.parse(content) as { summary?: unknown };
        if (typeof v?.summary !== 'string' || !v.summary.trim()) return null;
        return { summary: v.summary.trim().slice(0, MAX_SUMMARY_CHARS) };
    } catch {
        return null;
    }
}

/** The `survey-next` block's language. */
export const SURVEY_NEXT_LANGUAGE = 'survey-next';

const DRAFT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A `survey-next` block: `{ "draftId"? }`. Null while DANI is still writing it
 * (not yet JSON). A draftId that is not a draft id reads as none, so the card
 * falls back to the survey the chat is about instead of asking for a bad id.
 */
export function readSurveyNext(content: string): { draftId: string | null } | null {
    const text = content.trim();
    if (!text) return { draftId: null };
    try {
        const v = JSON.parse(text) as { draftId?: unknown } | null;
        if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
        const id = typeof v.draftId === 'string' ? v.draftId.trim() : '';
        return { draftId: DRAFT_ID.test(id) ? id : null };
    } catch {
        return null;
    }
}

/**
 * A `survey-status` block: `{ "draftId"?, "query"? }`, DANI showing how a
 * launched survey is fielding (docs/pdlc/fielding-status.md). `query` is the
 * survey's name or number as the person said it; neither means the survey this
 * conversation is about. Null while DANI is still writing it.
 */
export function readSurveyStatus(
    content: string,
): { draftId: string | null; query: string | null } | null {
    const text = content.trim();
    if (!text) return { draftId: null, query: null };
    try {
        const v = JSON.parse(text) as { draftId?: unknown; query?: unknown } | null;
        if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
        const id = typeof v.draftId === 'string' ? v.draftId.trim() : '';
        const query =
            typeof v.query === 'string' || typeof v.query === 'number'
                ? String(v.query).trim().slice(0, 120)
                : '';
        return { draftId: DRAFT_ID.test(id) ? id : null, query: query || null };
    } catch {
        return null;
    }
}

const NEXT: Record<HandoffKind, string> = {
    'feasibility-start':
        'The person wants to know whether a study is feasible. Carry on with them now, in this conversation: ask only what changes the number, then run the check with a feasibility-check block, or write a feasibility-plan when it needs working out.',
};

/**
 * The hidden message that hands DANI the rules: the rules message the chat
 * opened from Feasibility would start with (it keeps its header,
 * so the chat and every transcript leave it out), then what to do next.
 */
export function handoffMessage(kind: HandoffKind, rulesText: string, summary: string): string {
    return [
        rulesText.trim(),
        '',
        `What the person wants, as you summarised it: ${summary}`,
        '',
        NEXT[kind],
    ].join('\n');
}

const UNAVAILABLE: Record<HandoffTask, string> = {
    'survey-start':
        'Builder is not available to this person, so you cannot build the survey here. Tell them their MFour team can set it up with them, and do not hand a survey over again.',
    'feasibility-start':
        'Checking feasibility in the chat is not available to this person. Tell them their MFour team can check it, and do not write a feasibility block.',
};

/**
 * What DANI is told when the person's sign-in ended mid-step (a 401): not that
 * the tool is unavailable to them (UAT D11), only to sign in again and ask again.
 */
export function signedOutMessage(): string {
    return `${HANDOFF_HEADER}\n\nThe person's sign-in ended, so Studio could not do this. Nothing was changed. Tell them to sign in again and then ask again. Do not say the tool is unavailable to them.`;
}

/** What DANI is told when Studio cannot hand it the rules for this person (a 403). */
export function unavailableMessage(kind: HandoffTask): string {
    return `${HANDOFF_HEADER}\n\n${UNAVAILABLE[kind]}`;
}
